import {
  getStatusActiveBackgroundColor,
  getStatusColor,
} from '~/pages/admin/map/statusPerson';
import type { IPersonStatus } from '~/utils/interface/map';
import { JourneyPoint } from '~/utils/interface/mapback';

export type JourneySegmentType = JourneyPoint['segmentType'];

/** Map playback segment `type` → person-status `value` (same as map page). */
export const SEGMENT_TYPE_TO_STATUS_VALUE: Record<JourneySegmentType, number> = {
  route: 0,
  stop: 1,
  lost_gprs: 2,
  lost_gps: 3,
};

/** Fallback when person-status API is unavailable. */
export const JOURNEY_SEGMENT_TYPE_COLORS: Record<JourneySegmentType, string> = {
  route: '#22C55E',
  stop: '#9CA3AF',
  lost_gprs: '#F97316',
  lost_gps: '#EAB308',
};

const JOURNEY_SEGMENT_TYPE_ACTIVE_BG: Record<JourneySegmentType, string> = {
  route: 'rgba(34, 197, 94, 0.1)',
  stop: 'rgba(156, 163, 175, 0.1)',
  lost_gprs: 'rgba(249, 115, 22, 0.1)',
  lost_gps: 'rgba(234, 179, 8, 0.1)',
};

function darkenHexColor(hex: string, factor = 0.85): string {
  const normalized = hex.trim().replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return hex;

  const channels = [0, 2, 4].map((start) => Number.parseInt(normalized.slice(start, start + 2), 16));
  return `#${channels
    .map((channel) => Math.max(0, Math.min(255, Math.round(channel * factor)))
      .toString(16)
      .padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

function resolveStatusValue(type: JourneySegmentType): number {
  return SEGMENT_TYPE_TO_STATUS_VALUE[type] ?? SEGMENT_TYPE_TO_STATUS_VALUE.stop;
}

export function getJourneySegmentTypeColor(
  type: JourneySegmentType,
  personStatuses: IPersonStatus[] = []
): string {
  const statusValue = resolveStatusValue(type);
  return getStatusColor(statusValue, personStatuses) ?? JOURNEY_SEGMENT_TYPE_COLORS[type] ?? JOURNEY_SEGMENT_TYPE_COLORS.stop;
}

export function getJourneySegmentTypeActiveBg(
  type: JourneySegmentType,
  personStatuses: IPersonStatus[] = []
): string {
  const statusValue = resolveStatusValue(type);
  return (
    getStatusActiveBackgroundColor(statusValue, personStatuses) ??
    JOURNEY_SEGMENT_TYPE_ACTIVE_BG[type] ??
    JOURNEY_SEGMENT_TYPE_ACTIVE_BG.stop
  );
}

export function getJourneySegmentMarkerColors(
  type: JourneySegmentType,
  personStatuses: IPersonStatus[] = []
): { border: string; bg: string } {
  const bg = getJourneySegmentTypeColor(type, personStatuses);
  return {
    bg,
    border: darkenHexColor(bg),
  };
}
